import React from "react";
import { motion } from "framer-motion";

const W = 600, H = 340;
const px = (n) => ({ x: (n.x / 100) * W, y: (n.y / 100) * H });

function Node({ n, i, accent }) {
  const { x, y } = px(n);
  const w = Math.max(90, n.label.length * 8 + 28);
  const shape = n.shape === "circle"
    ? <circle r={38} fill="rgba(17,17,17,0.9)" stroke={accent} strokeWidth="1.5" />
    : <rect x={-w / 2} y={-20} width={w} height={40} rx={n.shape === "pill" ? 20 : 10} fill="rgba(17,17,17,0.9)" stroke={accent} strokeWidth="1.5" />;
  return (
    <g transform={`translate(${x} ${y})`} data-testid="diagram-node">
      <motion.g initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.25 + i * 0.35, type: "spring", stiffness: 160, damping: 16 }}>
        {shape}
        <text textAnchor="middle" dy="0.35em" fill="#E7E7E7" fontSize="12.5" fontWeight="300" letterSpacing="0.3">{n.label}</text>
      </motion.g>
    </g>
  );
}

export function AnimatedDiagram({ diagram, accent = "#FFFFFF", sceneKey }) {
  const nodes = diagram?.nodes || [];
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const base = 0.25 + nodes.length * 0.35;
  return (
    <svg key={sceneKey} viewBox={`0 0 ${W} ${H}`} className="w-full h-auto rounded-2xl border border-white/10 bg-[#000000]" data-testid="lesson-diagram">
      <defs>
        <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
          <path d="M0 0 L10 5 L0 10 z" fill={accent} />
        </marker>
        <pattern id="grid" width="30" height="30" patternUnits="userSpaceOnUse">
          <path d="M30 0 L0 0 0 30" fill="none" stroke="rgba(255,255,255,0.035)" />
        </pattern>
      </defs>
      <rect width={W} height={H} fill="url(#grid)" />
      {(diagram?.edges || []).map((e, i) => {
        const a = byId[e.from], b = byId[e.to];
        if (!a || !b) return null;
        const c1 = px(a), c2 = px(b);
        const d = Math.hypot(c2.x - c1.x, c2.y - c1.y) || 1, ux = (c2.x - c1.x) / d, uy = (c2.y - c1.y) / d, g = Math.min(44, d / 3);
        const p1 = { x: c1.x + ux * g, y: c1.y + uy * g }, p2 = { x: c2.x - ux * g, y: c2.y - uy * g };
        return (
          <g key={i}>
            <motion.line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke={accent} strokeOpacity="0.55" strokeWidth="1.4" markerEnd="url(#arrow)"
              initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: base + i * 0.3, duration: 0.6 }} />
            {e.label && (
              <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: base + i * 0.3 + 0.4 }}>
                <rect x={(p1.x + p2.x) / 2 - e.label.length * 3.4 - 6} y={(p1.y + p2.y) / 2 - 10} width={e.label.length * 6.8 + 12} height={20} rx={10} fill="#000000" stroke="rgba(255,255,255,0.08)" />
                <text x={(p1.x + p2.x) / 2} y={(p1.y + p2.y) / 2} dy="0.35em" textAnchor="middle" fill="#D3D3D3" fontSize="11" fontWeight="300">{e.label}</text>
              </motion.g>
            )}
          </g>
        );
      })}
      {nodes.map((n, i) => <Node key={n.id} n={n} i={i} accent={accent} />)}
    </svg>
  );
}
